/**
 * Google Ads click identifiers captured from the landing-page URL, kept in one Peak-owned
 * first-party cookie, and read back at handoff when Google's own cookie is missing.
 *
 * Runs on the merchant's own domain, inside the customer's browser, next to
 * `./google-click-cookies.ts`. Nothing here may import from `@/` — the bundle is self-contained
 * (`docs/STOREFRONT.md`).
 *
 * ## Why
 *
 * `./google-click-cookies.ts` reads the `_gcl_*` cookies Google's tag writes. A store with **no
 * Google tag at all** (no gtag, no GTM, no Google & YouTube channel pixel — a real client store
 * verified on 2026-09-22) never has those cookies, so a click on a Google ad produced a handoff
 * with no click id, while Shopify's native checkout still got the `gclid`: Shopify captures it
 * from the landing page itself and needs no tag for it. This module does the same. On every
 * page load it reads `gclid`, `wbraid`, `gbraid` and `dclid` off `location.search`, keeps them
 * in the cookie {@link PEAK_GCL_COOKIE} on the shop's host, and at handoff
 * `readAnalyticsCookies` (`./ga-cookies.ts`) fills in whichever id Google's cookie did not
 * supply. **Google's cookie wins per id** when both exist: it is the authoritative source, and
 * on a tagged store it carries the same id anyway.
 *
 * ## Why not `location.search` alone
 *
 * `location.search` is what the page's URL says *now*, and a theme can have rewritten it before
 * this `defer` script runs. Verified on a real client store on 2026-09-22: the theme's
 * product-page script — itself `defer`, and placed before Peak's tag, so it runs first
 * (deferred scripts execute in document order) — calls
 * `history.replaceState(state, '', productUrl + '?variant=' + id)` in its constructor to pin
 * the selected variant into the URL, which drops every other parameter, `gclid` included.
 * Google Shopping ads land on product pages, so that is exactly where the capture matters, and
 * the first cut of this module (reading `location.search` only) captured nothing there.
 *
 * The navigation-timing entry is the browser's own record of the landing URL and cannot be
 * rewritten by page scripts. MDN's PerformanceNavigationTiming page
 * (https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming, read
 * 2026-09-22) lists `PerformanceEntry.name` as: "Returns the document's URL. Note that text
 * fragments, and any other fragment directives, are stripped from the URL." A probe in Chromium
 * confirmed it against the theme behaviour above — a deferred script calling `replaceState`,
 * then a deferred reader: `location.search` was `?variant=1` while
 * `performance.getEntriesByType('navigation')[0].name` was the full landing URL with its
 * `gclid`, both for a direct navigation and for one reached through a 302 (the entry carries
 * the post-redirect URL, which is the one a Google click redirect lands on). So
 * {@link landingSearch} takes the query of that entry first and falls back to `location.search`
 * when there is no entry, its `name` has no `?`, or anything on the way throws. When **both**
 * carry a query, the entry's wins: a page script can only ever have changed `location`, never
 * the entry. Support floor from MDN's browser-compatibility table on that page (read
 * 2026-09-22): Chrome 57, Edge 12, Firefox 58, Safari 15, Safari on iOS 15.1, Opera 44, Samsung
 * Internet 7.0, Android WebView 57; an older engine takes the fallback and behaves as D166
 * first shipped. Residual: a landing rewritten by the theme *on* such an engine still loses the
 * ids.
 *
 * ## The cookie
 *
 * `_peak_gcl` — host-only (no `domain=`, so a sub-domain cannot read it), `path=/`,
 * `SameSite=Lax`, `Secure` on an https page, `max-age` of 90 days — the lifetime of Google's
 * own click cookies (Conversion Linker, https://support.google.com/tagmanager/answer/7549390).
 * The value is up to four segments joined by `~`, one per id, each `<key>.<unix seconds>.<id>`
 * with `key` being Google's own cookie suffix: `aw` = gclid, `gb` = wbraid, `ag` = gbraid,
 * `dc` = dclid. Example: `aw.1758500000.CjwKCAjw…~gb.1758500000.ClEKCQjw…`. The id charset is
 * {@link AD_CLICK_ID_PATTERN}, the one gtag.js itself enforces and the one the create endpoint
 * validates against. Parsing is strict: an unknown key, a timestamp that is not digits, an id
 * outside the charset, a wrong field count — any of it makes the **whole** cookie read as
 * absent, never throw. A new landing with a given id overwrites only that id's segment (a later
 * `gclid` click replaces the earlier `gclid`; an earlier `wbraid` stays). One storage, no
 * `localStorage` — consistent with D165 not reading Google's `_gcl_ls` either.
 *
 * ## Consent — Shopify's Customer Privacy API
 *
 * Google's `_gcl_*` cookies are "consent by construction": the tag sits behind the theme's
 * banner. A cookie Peak writes itself is not, so the write is gated on Shopify's Customer
 * Privacy API (https://shopify.dev/docs/api/customer-privacy, read 2026-09-22) when it is on
 * the page. Exactly two things are used:
 *
 * - **`window.Shopify.customerPrivacy.marketingAllowed()`** — one of the methods the page lists
 *   under "Call the API": "The Customer Privacy API offers the following methods to check if
 *   data can be processed: … `window.Shopify.customerPrivacy.marketingAllowed();` … These
 *   methods return true / false if permission is given for the following: …
 *   `marketingAllowed`: Marketing, attribution and targeted advertising from the merchant." A
 *   click id is attribution, so this is the purpose that governs it. The page also says which
 *   default applies before the visitor answers: "For regions that are configured to require
 *   consent, non-essential purposes are not allowed by default until consent is given. For
 *   other regions, the default behavior is to allow all processing purposes."
 * - **The `visitorConsentCollected` document event** — "The Customer Privacy API publishes the
 *   document event `visitorConsentCollected` when consent changes.
 *   `document.addEventListener("visitorConsentCollected", (event) => console.log(event.detail));`"
 *   with the detail shown as `{ marketingAllowed: true, saleOfDataAllowed: true,
 *   analyticsAllowed: false, preferencesAllowed: true }`, and "Updates are published only when
 *   consent changes, not when the listener is added." So a visitor who lands with `?gclid=` and
 *   accepts the banner a second later still gets the cookie: the ids stay in this module's
 *   closure for the page, and `event.detail.marketingAllowed === true` writes them.
 *
 * The API is not necessarily on the page when this `defer` script runs — Shopify's own loader
 * is asynchronous — and the doc is explicit about how it gets there: "To use the Customer
 * Privacy API, you must load it using `loadFeatures`" and "When invoked without an error, the
 * API is globally available at `window.Shopify.customerPrivacy`." Treating a not-yet-loaded API
 * as "no consent tool" would write the cookie before consent in a consent-required region, so
 * the rule, in order, is:
 *
 * 1. `window.Shopify.customerPrivacy.marketingAllowed` is a function → decide now: `true` →
 *    write; anything else (`false`, a non-boolean, a throw) → no write, listen for
 *    `visitorConsentCollected`, write when its detail says marketing is allowed.
 * 2. Otherwise `window.Shopify.loadFeatures` is a function → call it exactly as the doc shows,
 *    `loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => …)`, keep the
 *    ids in this closure, and apply rule 1 in the callback. A truthy `error`, or no
 *    `marketingAllowed` function after a clean callback, means the API cannot be had on this
 *    page: that is the documented no-API case, and the cookie is written. A `loadFeatures`
 *    that throws is treated the same way.
 * 3. Otherwise (no API and no loader: headless, the mock storefront) → write.
 *
 * Residual: a page with neither `loadFeatures` nor the API gets the cookie on the assumption
 * that the merchant runs no Shopify consent tool there. `setTrackingConsent` is never called:
 * "Recording customer consent should only be done on a visitor interaction …, and never done
 * automatically on behalf of the visitor."
 *
 * ## Hygiene
 *
 * Only this one cookie name is written and only this one and Google's four are ever read; on
 * `window.Shopify` only `customerPrivacy` and `loadFeatures` are touched, and on
 * `window.performance` only `getEntriesByType('navigation')`. No id is logged.
 * Nothing here can throw into the caller, and nothing the handoff needs waits on it: the
 * capture is one pass at page load (plus, at most, the loader's callback), and the handoff
 * reads the cookie the same way it reads Google's — analytics never stands between the
 * customer and the checkout.
 */

import { AD_CLICK_ID_PATTERN, type YcAdClickIds } from './google-click-cookies';

/** The one cookie this module writes. Host-only, `path=/`, 90 days. */
export const PEAK_GCL_COOKIE = '_peak_gcl';

/** 90 days (90 × 24 × 60 × 60), the lifetime Google gives its own click cookies. */
export const PEAK_GCL_MAX_AGE_SECONDS = 7776000;

/** The document event Shopify's Customer Privacy API publishes when consent changes. */
export const PEAK_CONSENT_EVENT = 'visitorConsentCollected';

/** Segment key (Google's own cookie suffix) → payload member, which is also the URL parameter. */
const KEYS: Record<string, keyof YcAdClickIds> = {
  aw: 'gclid',
  gb: 'wbraid',
  ag: 'gbraid',
  dc: 'dclid',
};

const DIGITS = /^\d+$/;

/** Stored segments by key: `aw` → `<unix seconds>.<id>`. */
type Stored = Record<string, string>;

/** The slice of `document` the capture touches. Structural so it can be tested without a DOM. */
export type YcCaptureDocumentLike = {
  cookie?: unknown;
  addEventListener?: (type: string, listener: (event: unknown) => void) => void;
};

/**
 * The slice of `window.performance` the capture touches. Structural so it can be tested without
 * a DOM; `undefined` on an engine without the API.
 */
export type YcPerformanceLike = {
  getEntriesByType?: (type: string) => unknown;
};

/** What {@link captureUrlClickIds} needs from the page. */
export type YcCaptureEnv = {
  /**
   * `location.search`, `?` included or not — the fallback source; the landing URL is taken
   * from `performance` first (see the module comment, "Why not `location.search` alone").
   */
  search: string;
  /** `window.performance`; only `getEntriesByType('navigation')` is called on it. */
  performance?: YcPerformanceLike | null | undefined;
  /** `location.protocol === 'https:'` — adds `Secure` to the cookie. */
  secure: boolean;
  /** `Date.now()`. */
  now: number;
  document: YcCaptureDocumentLike | null | undefined;
  /** `window.Shopify`, whatever it is; only `.customerPrivacy.marketingAllowed` is read. */
  shopifyGlobal: unknown;
};

/** Loose view of `window.Shopify`: the two members this module ever touches on it. */
type ShopifyLike =
  | { customerPrivacy?: { marketingAllowed?: unknown } | null; loadFeatures?: unknown }
  | null
  | undefined;

/** The Customer Privacy API, once it is on the page with the one method that is called. */
type PrivacyApi = { marketingAllowed: () => unknown };

/**
 * The query string of the URL this document was navigated to: the navigation-timing entry's
 * when the API is there and the entry's `name` carries a `?`, otherwise `search`. A `#…`
 * fragment after the query is excluded; a `?` that only appears inside the fragment is no
 * query. Never throws. The entry wins over `search` whenever it has a query, because a theme
 * may rewrite the URL before this script runs and the entry is the browser's own record of the
 * landing (module comment, "Why not `location.search` alone").
 */
export function landingSearch(
  performanceLike: YcPerformanceLike | null | undefined,
  search: string,
): string {
  try {
    if (performanceLike && typeof performanceLike.getEntriesByType === 'function') {
      const entry = (performanceLike.getEntriesByType('navigation') as { name?: unknown }[])[0];
      const name = entry ? entry.name : undefined;
      if (typeof name === 'string') {
        const hash = name.indexOf('#');
        const url = hash === -1 ? name : name.slice(0, hash);
        const query = url.indexOf('?');
        if (query !== -1) return url.slice(query);
      }
    }
  } catch {
    // An API that throws is no API; the page's own `location.search` is what is left.
  }
  return search;
}

/**
 * The click ids in a query string, or `undefined`.
 *
 * Only the four named parameters, only a value in the tag's own charset (`[\w-]`, at most
 * 200) — an empty value, `+`, `/`, a space or a percent-escape that decodes to any of them is
 * dropped. Everything else in the URL is ignored.
 */
export function parseUrlClickIds(search: string): YcAdClickIds | undefined {
  let ids: YcAdClickIds | undefined;
  const params = new URLSearchParams(search);
  for (const key in KEYS) {
    const member = KEYS[key] as keyof YcAdClickIds;
    const value = params.get(member);
    if (value !== null && AD_CLICK_ID_PATTERN.test(value)) {
      (ids || (ids = {}))[member] = value;
    }
  }
  return ids;
}

/**
 * The stored segments inside a `_peak_gcl` value, or `undefined` when the value is not one
 * this module wrote. Strict and whole: one bad segment discards all of them.
 */
export function parsePeakGclValue(value: string): Stored | undefined {
  if (typeof value !== 'string' || value === '') return undefined;
  const stored: Stored = {};
  for (const segment of value.split('~')) {
    const f = segment.split('.') as [string, string, string];
    if (
      f.length !== 3 ||
      typeof KEYS[f[0]] !== 'string' ||
      !DIGITS.test(f[1]) ||
      !AD_CLICK_ID_PATTERN.test(f[2])
    ) {
      return undefined;
    }
    stored[f[0]] = f[1] + '.' + f[2];
  }
  return stored;
}

/** The inverse of {@link parsePeakGclValue}: segments in {@link KEYS} order, `~`-joined. */
export function encodePeakGclValue(stored: Stored): string {
  const segments: string[] = [];
  for (const key in KEYS) if (stored[key] !== undefined) segments.push(key + '.' + stored[key]);
  return segments.join('~');
}

/** The full `document.cookie` assignment for a value. */
export function peakGclSetCookie(value: string, secure: boolean): string {
  // The attributes are one literal so the shipped bundle can be grepped for them
  // (`tests/unit/storefront/bundle.test.ts`); the unit test pins it to the constant above.
  return (
    PEAK_GCL_COOKIE +
    '=' +
    value +
    '; path=/; max-age=7776000; SameSite=Lax' +
    (secure ? '; Secure' : '')
  );
}

/**
 * The stored segments off a document, never throwing; `undefined` when the cookie is absent or
 * malformed. Exact name, first occurrence; no other cookie is looked at.
 */
function readStored(documentLike: YcCaptureDocumentLike | null | undefined): Stored | undefined {
  try {
    const jar = documentLike ? documentLike.cookie : undefined;
    if (typeof jar !== 'string') return undefined;
    for (const part of jar.split(';')) {
      const entry = part.trim();
      if (entry.indexOf(PEAK_GCL_COOKIE + '=') === 0) {
        return parsePeakGclValue(entry.slice(PEAK_GCL_COOKIE.length + 1).trim());
      }
    }
  } catch {
    // A cookie jar that throws is "no cookie".
  }
  return undefined;
}

/**
 * Read the click ids Peak stored off a document, in the same shape the Google reader returns,
 * never throwing. `undefined` when the cookie is absent or malformed.
 */
export function readPeakGclCookie(
  documentLike: YcCaptureDocumentLike | null | undefined,
): YcAdClickIds | undefined {
  const stored = readStored(documentLike);
  let ids: YcAdClickIds | undefined;
  if (stored) {
    for (const key in KEYS) {
      const value = stored[key];
      if (value !== undefined) {
        (ids || (ids = {}))[KEYS[key] as keyof YcAdClickIds] = value.slice(value.indexOf('.') + 1);
      }
    }
  }
  return ids;
}

/** Pure form of {@link readPeakGclCookie}, over a `document.cookie` string. */
export function parsePeakGclCookie(cookieString: string): YcAdClickIds | undefined {
  return readPeakGclCookie({ cookie: cookieString });
}

/** `window.Shopify.customerPrivacy` when it carries a `marketingAllowed` function, else `undefined`. */
function privacyApi(shopifyGlobal: unknown): PrivacyApi | undefined {
  const api = shopifyGlobal ? (shopifyGlobal as ShopifyLike)?.customerPrivacy : undefined;
  return api && typeof api.marketingAllowed === 'function' ? (api as PrivacyApi) : undefined;
}

/**
 * The page-load capture. Reads the landing URL once ({@link landingSearch}), then — in this
 * order — asks the Customer Privacy
 * API when it is on the page, loads it through `Shopify.loadFeatures` when it is not, and
 * writes without a gate only when neither exists. Writes now when consent allows it, or when
 * the `visitorConsentCollected` event later says marketing is allowed. Never throws, never
 * waits on anything the handoff needs, touches nothing when the URL carries no click id.
 */
export function captureUrlClickIds(env: YcCaptureEnv): void {
  try {
    const ids = parseUrlClickIds(landingSearch(env.performance, env.search));
    const documentLike = env.document;
    if (ids === undefined || !documentLike) return;

    const write = (): void => {
      try {
        const stored = readStored(documentLike) || {};
        for (const key in KEYS) {
          const id = ids[KEYS[key] as keyof YcAdClickIds];
          if (id !== undefined) stored[key] = Math.floor(env.now / 1000) + '.' + id;
        }
        documentLike.cookie = peakGclSetCookie(encodePeakGclValue(stored), env.secure);
      } catch {
        // A cookie jar that refuses the write is "no cookie"; the handoff is unaffected.
      }
    };

    // Step 2: the API is here — `true` writes; anything else (false, a non-boolean, a throw)
    // waits for the consent event.
    const decide = (api: PrivacyApi): void => {
      let allowed = false;
      try {
        allowed = api.marketingAllowed() === true;
      } catch {
        // A consent tool that throws is no licence to write.
      }
      if (allowed) write();
      else if (documentLike.addEventListener) {
        documentLike.addEventListener(PEAK_CONSENT_EVENT, (event) => {
          const detail = (event as { detail?: { marketingAllowed?: unknown } | null }).detail;
          if (detail && detail.marketingAllowed === true) write();
        });
      }
    };

    const shopify = env.shopifyGlobal;
    const api = privacyApi(shopify);
    if (api) {
      decide(api);
      return;
    }

    // Step 3: not loaded yet — load it exactly as the doc shows, and decide in the callback.
    // A load error, or no API after a clean callback, is the documented no-API fallback.
    const loadFeatures = shopify ? (shopify as ShopifyLike)?.loadFeatures : undefined;
    if (typeof loadFeatures === 'function') {
      try {
        (loadFeatures as (features: unknown, done: (error: unknown) => void) => void).call(
          shopify,
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            const loaded = error ? undefined : privacyApi(shopify);
            if (loaded) decide(loaded);
            else write();
          },
        );
        return;
      } catch {
        // A loader that throws cannot give us the API; fall through.
      }
    }

    // Step 4: no API and no loader — headless, a mock, a page with no Shopify consent tool.
    write();
  } catch {
    // Analytics never stands between the customer and the checkout.
  }
}
